import React from 'react';
import { MCQS, buildMcqPrompt } from './mcqData';
import { IconCheck, IconX, IconArrowRight, IconSpark, IconTarget } from './icons';

/*
 * McqCard — inline multiple-choice quiz card (question + A–D options with
 * immediate correct/incorrect feedback + AI Assistant chips). It is CONTROLLED:
 * idx/answers/marked live in NanoChat (per message) so the live progress and
 * question navigator can be rendered in the left sidebar. `active` false →
 * frozen read-only (a previous attempt kept in the thread).
 */
const letter = (i) => String.fromCharCode(65 + i);

const ASSIST = [
  { key: 'hint', label: 'Hint', prompt: 'Give a small hint to approach this question, WITHOUT revealing which option is correct.' },
  { key: 'explain', label: 'Explain', prompt: 'Explain the correct answer, and briefly why each other option is wrong.' },
  { key: 'walk', label: 'Walkthrough', prompt: 'Walk through the reasoning to reach the answer step by step.' },
];

const McqCard = ({ idx = 0, answers = {}, marked = [], active = true, onChange, disabled, onAsk }) => {
  const q = MCQS[idx];
  const total = MCQS.length;
  const selected = answers[idx];
  const answered = selected !== undefined;

  const choose = (i) => {
    if (!active || answered) return;
    onChange?.({ answers: { ...answers, [idx]: i } });
  };
  const clear = () => {
    const n = { ...answers };
    delete n[idx];
    onChange?.({ answers: n });
  };
  const next = () => onChange?.({ idx: Math.min(total - 1, idx + 1) });
  const toggleMark = () => {
    const has = marked.includes(idx);
    onChange?.({ marked: has ? marked.filter((x) => x !== idx) : [...marked, idx] });
  };

  const optClass = (i) => {
    if (!answered) return 'nano-mcq-opt';
    if (i === q.correct) return 'nano-mcq-opt correct';
    if (i === selected) return 'nano-mcq-opt wrong';
    return 'nano-mcq-opt';
  };

  return (
    <div className={`nano-mcqcard${active ? '' : ' inactive'}`}>
      <div className="nano-mcq-main">
        <div className="nano-mcq-head">
          <span className="nano-mcq-count">{idx + 1} / {total}</span>
          <button className={`nano-mcq-mark${marked.includes(idx) ? ' on' : ''}`} onClick={toggleMark} disabled={!active} type="button">
            <IconTarget size={14} /> Mark for Review
          </button>
        </div>

        <div className="nano-mcq-q">{q.question}</div>

        <div className="nano-mcq-opts">
          {q.options.map((opt, i) => (
            <button key={i} className={optClass(i)} onClick={() => choose(i)} disabled={!active} type="button">
              <span className="nano-mcq-key">{letter(i)}</span>
              <span className="nano-mcq-opt-txt">{opt}</span>
              {answered && i === q.correct && <span className="nano-mcq-tick"><IconCheck size={14} /></span>}
              {answered && i === selected && i !== q.correct && <span className="nano-mcq-cross"><IconX size={14} /></span>}
            </button>
          ))}
        </div>

        {answered && (
          <div className={`nano-mcq-feedback${selected === q.correct ? ' ok' : ' no'}`}>
            {selected === q.correct ? 'Correct! ' : `Not quite — the answer is ${letter(q.correct)}. `}
            {q.explanation}
          </div>
        )}

        <div className="nano-mcq-actions">
          <button className="nano-mcq-clear" onClick={clear} disabled={!active || !answered} type="button">Clear</button>
          <button className="nano-mcq-next" onClick={next} disabled={idx === total - 1} type="button">
            Next <IconArrowRight size={15} />
          </button>
        </div>
      </div>

      {/* AI Assistant — replies post into the chat thread */}
      <div className="nano-mcqcard-chips">
        <span className="nano-dsa-assist-lbl"><IconSpark size={14} /> AI Assistant</span>
        {ASSIST.map((a) => (
          <button key={a.key} className="nano-dsa-chip" onClick={() => onAsk(buildMcqPrompt(q, a.prompt), a.label)} disabled={disabled} type="button">
            {a.label}
          </button>
        ))}
      </div>
    </div>
  );
};

export default McqCard;
